import { type ReactNode, useMemo, useState } from 'react';

import type { LegalDocKey } from '@escala/domain/legal';
import { useCookieConsent } from '@/privacy/consent';

import { type LegalApi, LegalContext } from './context';
import CookieBanner from './CookieBanner';
import CookiePreferencesDialog from './CookiePreferencesDialog';
import LegalDialog from './LegalDialog';

/**
 * Camada de privacidade de toda a aplicação, por fora da sessão (vale no login, no
 * convite e no cadastro público): o aviso de cookies enquanto não há escolha, as
 * "Preferências de cookies" e os documentos legais, abertos por `useLegal()`.
 */
export default function PrivacyProvider({ children }: { children: ReactNode }) {
  const consent = useCookieConsent();
  const [legal, setLegal] = useState<{ doc: LegalDocKey; orgName?: string } | null>(null);
  const [cookies, setCookies] = useState(false);

  const api = useMemo<LegalApi>(
    () => ({
      openLegal: (doc = 'privacy', options = {}) => setLegal({ doc, orgName: options.orgName }),
      openCookies: () => setCookies(true),
    }),
    []
  );

  return (
    <LegalContext.Provider value={api}>
      {children}
      {!consent && !cookies ? (
        <CookieBanner onCustomize={() => setCookies(true)} onPolicy={() => setLegal({ doc: 'cookies' })} />
      ) : null}
      {legal ? (
        <LegalDialog
          doc={legal.doc}
          orgName={legal.orgName}
          onDocChange={(doc) => setLegal((l) => (l ? { ...l, doc } : l))}
          onClose={() => setLegal(null)}
          onManageCookies={() => {
            setLegal(null);
            setCookies(true);
          }}
        />
      ) : null}
      {cookies ? <CookiePreferencesDialog onClose={() => setCookies(false)} /> : null}
    </LegalContext.Provider>
  );
}
